import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { type AxiosError } from 'axios';
import { useGetClientAdminUser } from '~/hooks';
import { ROUTES } from '~/utils/routes';
import { convertToFormValues } from '../logics/convertToFormValues';
import { generatePutRequestBody } from '../logics/generatePutRequestBody';
import {
  type EditClientAdminUserFormFieldValues,
  editClientAdminUserSchema,
} from '../logics/schema';
import type { EditClientAdminUserFormPresentation } from '../presentations';
import { usePutClientAdminUser } from './usePutClientAdminUser';

type Args = {
  clientAdminUserId: number;
};

type PresentationProps = React.ComponentProps<typeof EditClientAdminUserFormPresentation>;

type IUseEditClientAdminUserForm = (args: Args) => {
  state: {
    data: ReturnType<typeof useGetClientAdminUser>['data'];
    methods: UseFormReturn<EditClientAdminUserFormFieldValues>;
    isSubmitDisabled: PresentationProps['isSubmitDisabled'];
  };
  actions: {
    handleSubmit: PresentationProps['onSubmit'];
  };
};

export const useEditClientAdminUserForm: IUseEditClientAdminUserForm = ({ clientAdminUserId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() || '';

  const { data } = useGetClientAdminUser({ threeLetterCode, id: clientAdminUserId });

  const methods = useForm<EditClientAdminUserFormFieldValues>({
    defaultValues: convertToFormValues(data),
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: zodResolver(editClientAdminUserSchema),
  });

  const { trigger, isMutating } = usePutClientAdminUser(threeLetterCode, clientAdminUserId, {
    onSuccess: () => {
      showToast({
        message: '企業管理者を更新しました。',
        status: 'success',
      });
      navigate(ROUTES.ADMINS.INDEX);
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors && errors.length > 0) {
          showToast({
            message: errors[0],
            status: 'danger',
          });
          return;
        }
      }

      showToast({
        message: '企業管理者の更新に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(formData => {
    trigger(generatePutRequestBody({ formValues: formData }));
  });

  return {
    state: {
      data,
      methods,
      isSubmitDisabled: !methods.formState.isValid || isMutating,
    },
    actions: {
      handleSubmit,
    },
  };
};
